import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { BACCARAT_CHIP_RATIO, BACCARAT_CHIP_RAW_VALUES, BACCARAT_CHIP_REAL_VALUES, BACCARAT_DEFAULT_CHIP_INDEX } from '@/config/original'
import type { BaccaratBet, BaccaratBetPlace, BaccaratGameState, BaccaratPlacedChip, BaccaratRoundState } from '@/types/baccarat'
import type { RootState } from '@/store/store'

export const initialBaccaratState: BaccaratGameState = {
	phase: 'ready',
	baccaratGameId: null,
	currencyCode: 'USD',
	selectedChipIndex: BACCARAT_DEFAULT_CHIP_INDEX,
	pendingChips: [],
	playerHand: [],
	bankerHand: [],
	playerScore: 0,
	bankerScore: 0,
	winner: null,
	playerPair: false,
	bankerPair: false,
	betResults: [],
	winningPlaces: [],
	status: 'idle',
	isSubmitting: false,
	isResultVisible: false,
	isClearing: false,
	isSpotHighlighting: false,
	currentMultiplier: 0,
	currentPayout: 0,
	currentProfit: 0,
	message: '',
}

function createPlacedChip(place: BaccaratBetPlace, chipIndex: number): BaccaratPlacedChip {
	const amount = BACCARAT_CHIP_REAL_VALUES[chipIndex] ?? 0
	return {
		id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}-${chipIndex}-${Math.random().toString(36).slice(2, 6)}`,
		place,
		chipIndex,
		amount,
		offsetX: Math.random() * 24 - 12,
		offsetY: Math.random() * 18 - 9,
		rotation: Math.random() * 50 - 25,
	}
}

/** Rebuild a place total as discrete chips (largest denominations first). */
function composeChipsForAmount(place: BaccaratBetPlace, amount: number): BaccaratPlacedChip[] {
	let remainingRaw = Math.round(amount * BACCARAT_CHIP_RATIO)
	if (remainingRaw <= 0) {
		return []
	}

	const chips: BaccaratPlacedChip[] = []

	for (let index = BACCARAT_CHIP_RAW_VALUES.length - 1; index >= 0; index -= 1) {
		const raw = BACCARAT_CHIP_RAW_VALUES[index]
		const count = Math.floor(remainingRaw / raw)
		for (let i = 0; i < count; i += 1) {
			chips.push(createPlacedChip(place, index))
		}
		remainingRaw -= count * raw
	}

	return chips
}

export function aggregateBaccaratChips(chips: BaccaratPlacedChip[]): BaccaratBet[] {
	const aggregated = new Map<BaccaratBetPlace, number>()

	for (const chip of chips) {
		aggregated.set(chip.place, Math.round(((aggregated.get(chip.place) ?? 0) + chip.amount) * 1e8) / 1e8)
	}

	return [...aggregated.entries()].map(([place, amount]) => ({ place, amount }))
}

function clearSettledVisuals(state: BaccaratGameState) {
	state.playerHand = []
	state.bankerHand = []
	state.playerScore = 0
	state.bankerScore = 0
	state.winner = null
	state.playerPair = false
	state.bankerPair = false
	state.betResults = []
	state.winningPlaces = []
	state.status = 'idle'
	state.isResultVisible = false
	state.isSpotHighlighting = false
	state.currentMultiplier = 0
	state.currentPayout = 0
	state.currentProfit = 0
	state.message = ''
	state.baccaratGameId = null
}

const baccaratSlice = createSlice({
	name: 'baccarat',
	initialState: initialBaccaratState,
	reducers: {
		resetBaccaratGame(state) {
			return {
				...initialBaccaratState,
				currencyCode: state.currencyCode,
				selectedChipIndex: state.selectedChipIndex,
			}
		},
		setBaccaratCurrencyCode(state, action: PayloadAction<string>) {
			state.currencyCode = action.payload
		},
		setBaccaratSelectedChipIndex(state, action: PayloadAction<number>) {
			const index = action.payload
			if (index < 0 || index >= BACCARAT_CHIP_REAL_VALUES.length) {
				return
			}
			state.selectedChipIndex = index
		},
		setBaccaratSubmitting(state, action: PayloadAction<boolean>) {
			state.isSubmitting = action.payload
		},
		setBaccaratClearing(state, action: PayloadAction<boolean>) {
			state.isClearing = action.payload
			if (action.payload) {
				state.isResultVisible = false
				state.isSpotHighlighting = false
			}
		},
		setBaccaratSpotHighlighting(state, action: PayloadAction<boolean>) {
			state.isSpotHighlighting = action.payload
		},
		addBaccaratPendingChip(state, action: PayloadAction<{ place: BaccaratBetPlace; chipIndex: number }>) {
			if (state.phase !== 'ready' || state.isSubmitting || state.isClearing) {
				return
			}

			const chipIndex = action.payload.chipIndex
			const amount = BACCARAT_CHIP_REAL_VALUES[chipIndex] ?? 0
			if (!Number.isFinite(amount) || amount <= 0) {
				return
			}

			if (state.status !== 'idle') {
				clearSettledVisuals(state)
			}

			state.pendingChips.push(createPlacedChip(action.payload.place, chipIndex))
		},
		undoBaccaratPendingChip(state) {
			if (state.phase !== 'ready' || state.isSubmitting || state.isClearing || state.pendingChips.length === 0) {
				return
			}
			state.pendingChips.pop()
		},
		clearBaccaratPendingChips(state) {
			if (state.phase !== 'ready' || state.isSubmitting || state.isClearing) {
				return
			}
			state.pendingChips = []
		},
		scaleBaccaratPendingChips(state, action: PayloadAction<number>) {
			if (state.phase !== 'ready' || state.isSubmitting || state.isClearing || state.pendingChips.length === 0) {
				return
			}

			const factor = action.payload
			if (!Number.isFinite(factor) || factor <= 0) {
				return
			}

			// Double: keep existing chips and append identical copies (same denominations).
			if (factor === 2) {
				const copies = state.pendingChips.map((chip) => createPlacedChip(chip.place, chip.chipIndex))
				state.pendingChips.push(...copies)
				return
			}

			// Halve: remove half of each identical stack; only odd leftovers are broken into smaller chips.
			if (factor === 0.5) {
				const groups = new Map<string, BaccaratPlacedChip[]>()
				const order: string[] = []

				for (const chip of state.pendingChips) {
					const key = `${chip.place}:${chip.chipIndex}`
					if (!groups.has(key)) {
						groups.set(key, [])
						order.push(key)
					}
					groups.get(key)!.push(chip)
				}

				const next: BaccaratPlacedChip[] = []
				for (const key of order) {
					const group = groups.get(key)!
					const keepCount = Math.floor(group.length / 2)
					const hasRemainder = group.length % 2 === 1

					next.push(...group.slice(0, keepCount))

					if (hasRemainder) {
						const sample = group[0]
						next.push(...composeChipsForAmount(sample.place, sample.amount / 2))
					}
				}

				state.pendingChips = next
				return
			}

			// Fallback for any other factor: rebuild from scaled totals.
			const scaledTotals = aggregateBaccaratChips(state.pendingChips).map((bet) => ({
				place: bet.place,
				amount: Math.round(bet.amount * factor * 1e8) / 1e8,
			}))
			state.pendingChips = scaledTotals.flatMap((bet) => composeChipsForAmount(bet.place, bet.amount))
		},
		clearBaccaratTable(state) {
			clearSettledVisuals(state)
		},
		clearBaccaratLosingChips(state) {
			const losingPlaces = new Set(state.betResults.filter((result) => result.outcome === 'lost').map((result) => result.place))
			if (losingPlaces.size === 0) {
				return
			}
			state.pendingChips = state.pendingChips.filter((chip) => !losingPlaces.has(chip.place))
		},
		applyBaccaratRoundState(state, action: PayloadAction<BaccaratRoundState>) {
			const data = action.payload

			state.playerHand = data.playerHand
			state.bankerHand = data.bankerHand
			state.playerScore = data.playerScore
			state.bankerScore = data.bankerScore
			state.winner = data.winner
			state.playerPair = data.playerPair
			state.bankerPair = data.bankerPair
			state.betResults = Array.isArray(data.betResults) ? data.betResults : []
			state.winningPlaces = Array.isArray(data.winningPlaces) ? data.winningPlaces : []
			state.currentMultiplier = data.multiplier
			state.currentPayout = data.payout
			state.currentProfit = data.profit
			state.baccaratGameId = data.baccaratGame?.id ?? null
			state.message = data.message ?? ''
			state.status = data.status
			state.phase = 'ready'
			state.isResultVisible = false
			state.isSpotHighlighting = false
		},
		showBaccaratResult(state) {
			state.isResultVisible = true
		},
		dismissBaccaratResult(state) {
			state.isResultVisible = false
		},
	},
})

export const {
	resetBaccaratGame,
	setBaccaratCurrencyCode,
	setBaccaratSelectedChipIndex,
	setBaccaratSubmitting,
	setBaccaratClearing,
	setBaccaratSpotHighlighting,
	addBaccaratPendingChip,
	undoBaccaratPendingChip,
	clearBaccaratPendingChips,
	scaleBaccaratPendingChips,
	clearBaccaratTable,
	clearBaccaratLosingChips,
	applyBaccaratRoundState,
	showBaccaratResult,
	dismissBaccaratResult,
} = baccaratSlice.actions

export const selectBaccaratGame = (state: RootState) => state.baccarat

export default baccaratSlice.reducer
